import React from "react";
import { StyleSheet, View } from "react-native";
import { StackSimpleIcon, ShieldIcon } from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type { CourseEvaluatorReviewSidebarItemMock } from "../mockData/courseEvaluatorReview.mockData";
import type { ActivityDetailMock, StructuredFields } from "../types/reviewTypes";
import SegmentedDiffView from "./SegmentedDiffView";

interface ModuleDescriptionPanelProps {
  selectedReviewItem: CourseEvaluatorReviewSidebarItemMock;
  activityDetail: ActivityDetailMock | null;
}

function ModuleDescriptionPanel({ selectedReviewItem, activityDetail }: ModuleDescriptionPanelProps) {
  const detail = activityDetail;

  const currentFields: StructuredFields = {
    title: detail?.currentTitle ?? selectedReviewItem.title,
    description: detail?.currentDescription ?? "",
  };

  const proposedFields: StructuredFields = {
    title: detail?.recommendedTitle ?? selectedReviewItem.title,
    description: detail?.recommendedDescription ?? "",
  };

  return (
    <View style={styles.panel}>
      {/* Type badge */}
      <View style={styles.typeBadge}>
        <StackSimpleIcon size={14} color="#7C3AED" weight="bold" />
        <CustomText
          text="Module Description"
          useTranslationText={false}
          size="xs"
          weight="medium"
          style={styles.typeBadgeText}
        />
      </View>

      {/* Title */}
      <CustomText
        text={selectedReviewItem.title}
        useTranslationText={false}
        size="xl"
        weight="bold"
        style={styles.title}
      />

      {/* Why card */}
      {detail?.feedback && (
        <View style={styles.whyCard}>
          <CustomText
            text="Why we're recommending changes"
            useTranslationText={false}
            size="s"
            weight="bold"
            style={styles.whyHeading}
          />
          <CustomText text={detail.feedback} useTranslationText={false} size="xs" style={styles.whyBody} />
          {detail.rubricCode && (
            <View style={styles.rubricRow}>
              <ShieldIcon size={12} color={colors.brand[600]} weight="fill" />
              <CustomText
                text={`${detail.rubricCode}: ${detail.rubricTitle}`}
                useTranslationText={false}
                size="xs"
                weight="medium"
                style={styles.rubricLink}
              />
              {detail.rubricDescription && (
                <CustomText
                  text={` \u2014 ${detail.rubricDescription}`}
                  useTranslationText={false}
                  size="xs"
                  style={styles.rubricDescription}
                />
              )}
            </View>
          )}
        </View>
      )}

      {/* Segmented diff view */}
      <SegmentedDiffView variant="module" currentFields={currentFields} proposedFields={proposedFields} />

      {/* Last modified */}
      {detail?.lastModifiedBy && (
        <CustomText
          text={`Last modified by ${detail.lastModifiedBy} on ${detail.lastModifiedAt ?? "unknown date"}`}
          useTranslationText={false}
          size="xs"
          style={styles.lastModified}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    gap: 16,
  },
  typeBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    alignSelf: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
    backgroundColor: "#F3EEFF",
  },
  typeBadgeText: {
    color: "#7C3AED",
  },
  title: {
    color: colors.grey[900],
  },
  whyCard: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    padding: 16,
    gap: 6,
  },
  whyHeading: {
    color: colors.grey[900],
    marginBottom: 2,
  },
  whyBody: {
    color: colors.grey[600],
    lineHeight: 20,
  },
  rubricRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 4,
  },
  rubricLink: {
    color: colors.brand[600],
  },
  rubricDescription: {
    color: colors.grey[500],
  },
  lastModified: {
    color: colors.grey[400],
  },
});

export default ModuleDescriptionPanel;
